import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { Input } from '@/components/ui/input'
import { Loader2, Search } from 'lucide-react'
import { nodeOptions, type NodeOption } from '../../data/nodeOptions'
import NodeList from '../ui/NodeList'
// PROJ-085 Wave 1: Renamed from useWorkflowNodeOptions
import { useVisualNodeOptions } from '@/hooks/useVisualNodeOptions'

interface AddNodePanelContentProps {
  onClose: () => void
  onSelect: (option: NodeOption) => void
}

export default function AddNodePanelContent({ onClose, onSelect }: AddNodePanelContentProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const { nodeOptions: dynamicOptions, loading, error } = useVisualNodeOptions()

  // Auto-focus input when panel opens
  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus()
    }, 100)
    return () => clearTimeout(timer)
  }, [])

  // Merge dynamic options with static fallback options - memoized to prevent recreation
  const allOptions = useMemo(() =>
    loading || error ? nodeOptions : [...dynamicOptions, ...nodeOptions],
    [loading, error, dynamicOptions]
  );

  // Memoized handler to prevent child re-renders
  const handleSelect = useCallback((option: NodeOption) => {
    onSelect(option) // Pass the full NodeOption instead of just the type
    onClose()
    setSearchQuery('')
  }, [onSelect, onClose]);

  return (
    <div
      className="w-[312px] bg-background border border-border rounded-xl shadow-2xl overflow-hidden"
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="px-3 pt-3 pb-1.5 cursor-default">
        <h2 className="text-sm font-semibold">Add Node</h2>
      </div>

      {/* Search */}
      <div className="px-3 py-1.5 border-t border-b" onMouseDown={(e) => e.stopPropagation()}>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            ref={inputRef}
            id="add-node-search"
            name="add-node-search"
            placeholder="Buscar nodos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault()
                onClose()
              }
            }}
            className="pl-8 h-8 text-sm"
            autoComplete="off"
          />
        </div>
      </div>

      {/* Node list - using native scroll with nowheel class for React Flow compatibility */}
      <div
        className="max-h-[60vh] overflow-y-auto overflow-x-hidden px-1 nowheel"
        onWheelCapture={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            <span className="text-sm">Cargando nodos...</span>
          </div>
        ) : error ? (
          <div className="px-3 py-6 text-center text-xs text-destructive">
            Error al cargar nodos. Usando opciones por defecto.
          </div>
        ) : null}
        <NodeList options={allOptions} searchQuery={searchQuery} onSelect={handleSelect} />
      </div>
    </div>
  )
}
